import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrentInternalUser, isAdmin } from '../lib/currentUser';
import Sidebar from '../components/Sidebar';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import TagChip from '../components/TagChip';
import { useActionToast } from '../components/ActionToast';
import BackIconButton from '../components/BackIconButton';
import { OttoLoading } from '../components/otto';
import {
  fetchVocabulary, usageCounts, createTag, updateTag, setArchived, deleteTag, mergeTags,
  TAG_COLOUR_PRESETS, TAG_DEFAULT_COLOUR, TAG_MAX_LEN,
} from '../lib/tags';
import { fmtDate } from '../lib/dates';

const VOCABS = [
  { key: 'supplier', title: 'Supplier Tags', noun: 'supplier' },
  { key: 'brief', title: 'Brief Tags', noun: 'brief' },
];
const SORTS = [
  { key: 'az', label: 'A–Z' },
  { key: 'recent', label: 'Most recent' },
  { key: 'most', label: 'Most used' },
  { key: 'least', label: 'Least used' },
];
// All created-dates on this admin steward view render as absolute
// long-form ("29 May 2026") via the shared fmtDate. The earlier
// relative-vs-absolute mix made the list scan inconsistently — newer
// tags read as "4 days ago" while older ones jumped to the absolute
// date. Locked to one format for the audit register.
const fmtCreated = (iso) => fmtDate(iso, '');

const sortTags = (tags, key, usage) => {
  const cmp = {
    az: (a, b) => a.name.localeCompare(b.name),
    recent: (a, b) => new Date(b.created_at) - new Date(a.created_at),
    most: (a, b) => (usage.get(b.id) || 0) - (usage.get(a.id) || 0),
    least: (a, b) => (usage.get(a.id) || 0) - (usage.get(b.id) || 0),
  }[key] || ((a, b) => a.name.localeCompare(b.name));
  const active = tags.filter(t => !t.archived).sort(cmp);
  const archived = tags.filter(t => t.archived).sort((a, b) => a.name.localeCompare(b.name));
  return [...active, ...archived];
};

export default function AdminTags() {
  const navigate = useNavigate();
  const { user: currentUser, loading: currentUserLoading } = useCurrentInternalUser();
  const actorId = currentUser?.id;

  useEffect(() => {
    if (currentUserLoading) return;
    if (!currentUser || !isAdmin(currentUser)) navigate('/dashboard', { replace: true });
  }, [currentUser, currentUserLoading, navigate]);

  const [data, setData] = useState({ supplier: { tags: [], usage: new Map() }, brief: { tags: [], usage: new Map() } });
  const [sort, setSort] = useState({ supplier: 'az', brief: 'az' });
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null); // { vocabulary, tag|null, name, colour, error, saving }
  const [merge, setMerge] = useState(null);    // { vocabulary, source, targetId, saving }
  const [del, setDel] = useState(null);        // { vocabulary, tag }
  // Action-confirmation toast. Every mutator on this page (create /
  // update / merge / archive / delete) used to succeed silently — the
  // list re-rendered but staff got no "yes that worked" affirmation.
  const { notify: notifyToast, Toast: ActionToast } = useActionToast();

  const load = useCallback(async () => {
    setLoading(true);
    const [sTags, sUsage, bTags, bUsage] = await Promise.all([
      fetchVocabulary('supplier'), usageCounts('supplier'),
      fetchVocabulary('brief'), usageCounts('brief'),
    ]);
    setData({ supplier: { tags: sTags, usage: sUsage }, brief: { tags: bTags, usage: bUsage } });
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveEditor = async () => {
    setEditor(e => ({ ...e, saving: true, error: null }));
    const { tag, vocabulary, name, colour } = editor;
    const wasUpdate = !!tag;
    const res = wasUpdate
      ? await updateTag(tag, { name, colour }, actorId)
      : await createTag({ name, vocabulary, colour, actorId });
    if (res.error) { setEditor(e => ({ ...e, saving: false, error: res.error.message })); return; }
    setEditor(null);
    load();
    notifyToast(wasUpdate
      ? { eyebrow: 'Tag updated', title: `#${name} saved`, body: 'Any brief or supplier using this tag picks up the new label and colour on next view.' }
      : { eyebrow: 'Tag created', title: `#${name} added to the ${vocabulary} vocabulary` }
    );
  };

  const doMerge = async () => {
    const target = data[merge.vocabulary].tags.find(t => t.id === merge.targetId);
    if (!target) return;
    const sourceName = merge.source?.name || 'tag';
    const targetName = target.name;
    setMerge(m => ({ ...m, saving: true }));
    await mergeTags(merge.source, target, actorId);
    setMerge(null);
    load();
    notifyToast({
      eyebrow: 'Tags merged',
      title: `#${sourceName} merged into #${targetName}`,
      body: `Every record previously tagged #${sourceName} now uses #${targetName}.`,
    });
  };

  const doDelete = async () => {
    const tagName = del?.tag?.name || 'Tag';
    await deleteTag(del.tag, actorId);
    setDel(null);
    load();
    notifyToast({
      eyebrow: 'Tag deleted',
      title: `#${tagName} removed`,
      body: 'Existing records lose this tag automatically — no further cleanup needed.',
    });
  };

  const doArchive = async (tag, archived) => {
    await setArchived(tag, archived, actorId);
    load();
    notifyToast(archived
      ? { eyebrow: 'Tag archived', title: `#${tag.name} archived`, body: "Hidden from the picker but still rendered on records that already have it." }
      : { eyebrow: 'Tag restored', title: `#${tag.name} back in the picker` }
    );
  };

  if (currentUserLoading || !currentUser) return null;

  const renderColumn = ({ key, title, noun }) => {
    const { tags, usage } = data[key];
    const rows = sortTags(tags, sort[key], usage);
    const activeCount = tags.filter(t => !t.archived).length;
    return (
      <div className="bg-white border border-line rounded-xl p-5">
        <div className="flex items-center justify-between mb-3.5">
          <span className="font-['Metropolis'] text-[13px] font-extrabold tracking-[0.18em] uppercase text-fusion-forest">{title}</span>
          <button
            type="button"
            onClick={() => setEditor({ vocabulary: key, tag: null, name: '', colour: TAG_DEFAULT_COLOUR, error: null, saving: false })}
            className="bg-[#92D400] text-[#131313] rounded-full px-3.5 py-1.5 text-[11px] font-extrabold tracking-[0.12em] uppercase font-['Metropolis'] hover:bg-[#a4e810] transition-colors"
          >
            + Create Tag
          </button>
        </div>

        {/* Sort controls */}
        <div className="flex items-center gap-2 flex-wrap mb-3.5 pb-3.5 border-b border-line">
          <span className="font-['Metropolis'] text-[10px] font-bold tracking-[0.18em] uppercase text-[#51534A]">Sort by:</span>
          {SORTS.map(s => {
            const active = sort[key] === s.key;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => setSort(prev => ({ ...prev, [key]: s.key }))}
                className={`rounded-full px-2.5 py-1 text-[11px] font-bold font-['Metropolis'] border transition-colors ${active ? 'bg-white border-[#DADCCD] text-ink' : 'bg-transparent border-line text-fusion-mid-grey hover:border-[#DADCCD]'}`}
              >
                {s.label}
              </button>
            );
          })}
        </div>

        {rows.length === 0 ? (
          <p className="font-['Metropolis'] text-xs text-[#51534A] italic py-4">No {noun} tags yet. Create the first, or they'll appear here as the team adds them.</p>
        ) : (
          <div>
            {rows.map(tag => {
              const count = usage.get(tag.id) || 0;
              const records = `${count} ${noun}${count === 1 ? '' : 's'}`;
              return (
                <div key={tag.id} className={`flex items-center gap-3 py-2.5 border-b border-line last:border-0 ${tag.archived ? 'opacity-50' : ''}`}>
                  <div className="flex-shrink-0 flex items-center gap-1.5">
                    <TagChip tag={tag} />
                    {tag.archived && <span className="font-['Metropolis'] text-[9px] font-bold tracking-[0.15em] uppercase text-[#51534A]">Archived</span>}
                  </div>
                  <span className="flex-1 min-w-0 font-['Metropolis'] text-[11px] text-[#51534A] truncate">
                    {tag.archived
                      ? `Created ${fmtCreated(tag.created_at)} · ${records} (still tagged)`
                      : `Created ${fmtCreated(tag.created_at)}${tag.creator?.full_name ? ` · ${tag.creator.full_name}` : ''} · ${records}`}
                  </span>
                  <div className="flex gap-2 flex-shrink-0">
                    {tag.archived ? (
                      <>
                        <button type="button" onClick={() => doArchive(tag, false)} className="border border-line text-fusion-mid-grey rounded-full px-2.5 py-1 text-[10px] font-bold font-['Metropolis'] hover:border-[#DADCCD] hover:text-ink transition-colors">Restore</button>
                        <button type="button" onClick={() => setDel({ vocabulary: key, tag })} className="border border-line text-fusion-mid-grey rounded-full px-2.5 py-1 text-[10px] font-bold font-['Metropolis'] hover:border-[#E0290A] hover:text-[#E0290A] transition-colors">Delete</button>
                      </>
                    ) : (
                      <>
                        <button type="button" onClick={() => setEditor({ vocabulary: key, tag, name: tag.name, colour: tag.colour, error: null, saving: false })} className="border border-line text-fusion-mid-grey rounded-full px-2.5 py-1 text-[10px] font-bold font-['Metropolis'] hover:border-[#DADCCD] hover:text-ink transition-colors">Edit</button>
                        <button type="button" onClick={() => setMerge({ vocabulary: key, source: tag, targetId: '', saving: false })} className="border border-line text-fusion-mid-grey rounded-full px-2.5 py-1 text-[10px] font-bold font-['Metropolis'] hover:border-[#DADCCD] hover:text-ink transition-colors">Merge</button>
                        <button type="button" onClick={() => doArchive(tag, true)} className="border border-line text-fusion-mid-grey rounded-full px-2.5 py-1 text-[10px] font-bold font-['Metropolis'] hover:border-[#DADCCD] hover:text-ink transition-colors">Archive</button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <p className="font-['Metropolis'] text-[10px] text-[#51534A] mt-3">{activeCount} active {activeCount === 1 ? 'tag' : 'tags'}</p>
      </div>
    );
  };

  const mergeTarget = merge ? data[merge.vocabulary].tags.find(t => t.id === merge.targetId) : null;
  const mergeNoun = merge ? VOCABS.find(v => v.key === merge.vocabulary).noun : '';
  const mergeCount = merge ? (data[merge.vocabulary].usage.get(merge.source.id) || 0) : 0;
  const delNoun = del ? VOCABS.find(v => v.key === del.vocabulary).noun : '';
  const delCount = del ? (data[del.vocabulary].usage.get(del.tag.id) || 0) : 0;

  return (
    <div className="h-screen bg-canvas flex overflow-hidden">
      {/* Action-confirmation toast — bound to useActionToast above. */}
      <ActionToast />
      <Sidebar currentInternalUser={currentUser} />

      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="pt-8 flex-shrink-0">
          <div className="px-8 mb-6">
            <div className="mb-3"><BackIconButton to="/admin" label="Back to admin" /></div>
            <h1 className="fusion-h1 text-fusion-forest">Tags</h1>
            <p className="font-['Metropolis'] text-sm text-fusion-mid-grey mt-2">Two vocabularies. Anyone applies and creates tags through the picker; here you steward them — edit, merge duplicates, archive, delete.</p>
          </div>
          <div className="mx-8 h-px bg-[#E6E9DA]" aria-hidden="true" />
        </div>

        <div className="flex-1 overflow-y-auto p-8">
          {loading ? (
            <div className="py-20"><OttoLoading size="large" message="Loading tags" /></div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {VOCABS.map(v => <React.Fragment key={v.key}>{renderColumn(v)}</React.Fragment>)}
            </div>
          )}
        </div>
      </div>

      {/* Create / Edit modal */}
      <Modal open={!!editor} onClose={() => setEditor(null)} maxWidth={460} ariaLabel="Tag editor">
        {editor && (
          <>
            <Modal.Header
              eyebrow={VOCABS.find(v => v.key === editor.vocabulary).title}
              title={editor.tag ? 'Edit tag' : 'Create tag'}
              onClose={() => setEditor(null)}
            />
            <Modal.Body>
              <label className="font-['Metropolis'] text-[10px] font-bold tracking-[0.18em] uppercase text-[#51534A] mb-2 block">Name</label>
              <input
                autoFocus
                value={editor.name}
                maxLength={TAG_MAX_LEN}
                onChange={e => setEditor(ed => ({ ...ed, name: e.target.value, error: null }))}
                className="w-full bg-white border border-line rounded-md px-3 py-2 text-sm text-ink outline-none focus:border-[#92D400] font-['Metropolis']"
                placeholder="e.g. Catering"
              />
              <p className="font-['Metropolis'] text-[10px] text-[#51534A] mt-1">{editor.name.length}/{TAG_MAX_LEN}</p>

              <label className="font-['Metropolis'] text-[10px] font-bold tracking-[0.18em] uppercase text-[#51534A] mt-5 mb-2 block">Colour</label>
              {/* Tag-safe palette only — exactly the four hexes the DB
                  CHECK whitelists (blue / rose / slate / sand). The
                  custom-colour picker + freeform hex input were removed
                  in 2026-06: they let a custom magenta land on a real
                  tag, which is exactly what the whitelist closes off.
                  If the palette needs to grow, add the hex here AND in
                  the DB CHECK in the same commit. */}
              <div className="flex items-center gap-2.5 flex-wrap">
                {TAG_COLOUR_PRESETS.map(hex => (
                  <button
                    key={hex}
                    type="button"
                    aria-label={hex}
                    onClick={() => setEditor(ed => ({ ...ed, colour: hex, error: null }))}
                    style={{ background: hex }}
                    className={`w-7 h-7 rounded-full ring-2 ring-offset-2 ring-offset-white transition-all ${editor.colour === hex ? 'ring-[#131313]' : 'ring-transparent'}`}
                  />
                ))}
              </div>
              <div className="mt-5">
                <span className="font-['Metropolis'] text-[10px] font-bold tracking-[0.18em] uppercase text-[#51534A] mr-2">Preview</span>
                <TagChip name={editor.name || 'Tag'} colour={editor.colour} />
              </div>
              {editor.error && <p className="font-['Metropolis'] text-xs text-[#E0290A] mt-4">{editor.error}</p>}
            </Modal.Body>
            <Modal.Footer>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setEditor(null)} className="bg-white border border-line rounded font-['Metropolis'] text-xs font-bold tracking-[0.15em] uppercase text-ink px-4 py-2 hover:border-[#DADCCD] transition-colors">Cancel</button>
                {/* Save guard simplified — the picker now only offers
                    the four tag-safe presets, so any colour in state
                    is guaranteed to pass the DB whitelist CHECK. Name
                    is the only field that can be empty. */}
                <button type="button" onClick={saveEditor} disabled={editor.saving || !editor.name.trim()} className="bg-[#92D400] border border-[#92D400] rounded font-['Metropolis'] text-xs font-bold tracking-[0.15em] uppercase text-[#131313] px-4 py-2 hover:bg-[#a4e810] transition-colors disabled:opacity-50">{editor.saving ? 'Saving…' : (editor.tag ? 'Save' : 'Create')}</button>
              </div>
            </Modal.Footer>
          </>
        )}
      </Modal>

      {/* Merge modal */}
      <Modal open={!!merge} onClose={() => setMerge(null)} maxWidth={480} ariaLabel="Merge tags">
        {merge && (
          <>
            <Modal.Header eyebrow="Merge — irreversible" title={`Merge "${merge.source.name}" into…`} onClose={() => setMerge(null)} />
            <Modal.Body>
              <p className="font-['Metropolis'] text-xs text-fusion-mid-grey mb-4 leading-relaxed">
                Pick the tag to keep. Every {mergeNoun} tagged <strong className="text-ink">{merge.source.name}</strong> will be retagged with it, and <strong className="text-ink">{merge.source.name}</strong> will be deleted.
              </p>
              <label className="font-['Metropolis'] text-[10px] font-bold tracking-[0.18em] uppercase text-[#51534A] mb-2 block">Merge into</label>
              <select
                value={merge.targetId}
                onChange={e => setMerge(m => ({ ...m, targetId: e.target.value }))}
                className="w-full bg-white border border-line rounded-md px-3 py-2 text-sm text-ink outline-none focus:border-[#92D400] font-['Metropolis']"
              >
                <option value="">Choose a target tag…</option>
                {data[merge.vocabulary].tags
                  .filter(t => !t.archived && t.id !== merge.source.id)
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              {mergeTarget && (
                <p className="font-['Metropolis'] text-[11px] text-[#7A4E10] mt-4 leading-relaxed bg-[#E89A2E]/[0.06] border border-[#E89A2E]/30 rounded-md px-3 py-2">
                  Every {mergeNoun} currently tagged "{merge.source.name}" ({mergeCount} {mergeCount === 1 ? mergeNoun : `${mergeNoun}s`}) will be retagged with "{mergeTarget.name}". The "{merge.source.name}" tag will be deleted. Cannot be undone.
                </p>
              )}
            </Modal.Body>
            <Modal.Footer>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setMerge(null)} className="bg-white border border-line rounded font-['Metropolis'] text-xs font-bold tracking-[0.15em] uppercase text-ink px-4 py-2 hover:border-[#DADCCD] transition-colors">Cancel</button>
                <button type="button" onClick={doMerge} disabled={!merge.targetId || merge.saving} className="bg-[#E89A2E] border border-[#E89A2E] rounded font-['Metropolis'] text-xs font-bold tracking-[0.15em] uppercase text-[#131313] px-4 py-2 hover:brightness-110 transition-all disabled:opacity-50">{merge.saving ? 'Merging…' : 'Merge'}</button>
              </div>
            </Modal.Footer>
          </>
        )}
      </Modal>

      {/* Delete confirm */}
      <ConfirmDialog
        open={!!del}
        title={del ? `Delete "${del.tag.name}"?` : ''}
        body={del ? `This permanently deletes the tag and removes it from ${delCount} ${delCount === 1 ? delNoun : `${delNoun}s`}. This cannot be undone.` : ''}
        confirmLabel="Delete"
        tone="danger"
        onConfirm={doDelete}
        onCancel={() => setDel(null)}
      />
    </div>
  );
}
